import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Col, List, Row } from 'antd'
import { RedoOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import styled from 'styled-components'
import NotificationIcon from './NotificationIcon'
import ContentBlock from 'components/ContentBlock'
import FullWidthSpace from 'components/FullWidthSpace'
import InfiniteScroll from 'react-infinite-scroller'
import { generatePath, useHistory } from 'react-router'
import LoadingIcon from 'components/LoadingIcon'
import { NotificationDataSource } from './interface'
import { useGetMyNotificationQuery } from 'graphQL/useGetMyNotification'

import { routePaths } from 'pages/routeConfig'

import { Pagination } from 'graphQL/graphQL-service-hook'
import { useReadNotificationMutation } from 'graphQL/useReadNotification'
import { MyNotificationAPIPayload } from 'graphQL/useGetMyNotification/interface'

const NotificationList: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [notificactions, setNotifications] = useState<NotificationDataSource[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    limit: 10,
    page: 1,
  })
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const myNotificationResp = useGetMyNotificationQuery({
    fetchPolicy: 'network-only',
    variables: {
      input: {
        pagination: {
          limit: pagination.limit,
          page: pagination.page,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
    onCompleted(resp) {
      const data = resp.getMyNotification
      setPagination({ ...pagination, totalPages: data.pagination?.totalPages })
      if (data.pagination?.page === 1) {
        setNotifications(data.payload)
      } else {
        setNotifications(notificactions?.concat(data.payload))
      }
      setLoading(false)
    },
  })
  const [readNotification] = useReadNotificationMutation({
    fetchPolicy: 'network-only',
    onCompleted(resp) {
      const data = resp.readNotification
      history.push(
        generatePath(routePaths.notificationDetail, {
          id: data.payload[0]._id,
        })
      )
    },
  })

  const handleInfiniteOnLoad = () => {
    setLoading(true)
    if (notificactions.length === pagination.totalItems) {
      setLoading(false)
      setHasMore(false)
      return
    }
    setPagination({ ...pagination, page: Number(pagination.page) + 1 })
  }

  const handleReadNotificaiton = (notification: MyNotificationAPIPayload) => {
    if (notification.read === 'NO') {
      readNotification({
        variables: {
          notificationIdList: [notification._id],
        },
      })
    } else {
      history.push(
        generatePath(routePaths.notificationDetail, {
          id: notification._id,
        })
      )
    }
  }

  const handleRefresh = () => {
    setLoading(true)
    setPagination({ ...pagination, page: 1 })
    myNotificationResp.refetch()
  }

  const readStatus = (notification: MyNotificationAPIPayload): string => {
    let textDate = dayjs(notification.updatedAt).fromNow()
    textDate = notification.read === 'YES' ? t('notification:read') + ' ' + textDate : textDate
    return textDate
  }

  return (
    <ContentBlock>
      <Row gutter={32}>
        <Col span={24} style={{ textAlign: 'right' }}>
          <Button type="primary" icon={<RedoOutlined />} loading={myNotificationResp.loading} onClick={handleRefresh}>
            {t('button:refresh')}
          </Button>
        </Col>
        <Col span={24} style={{ marginTop: '5px' }}>
          <InfiniteScroll
            initialLoad={false}
            pageStart={0}
            loadMore={handleInfiniteOnLoad}
            hasMore={!loading && hasMore}
          >
            <List
              dataSource={notificactions}
              renderItem={(item) => (
                <ListItemStyle key={item._id} onClick={() => handleReadNotificaiton(item)}>
                  <List.Item.Meta
                    avatar={
                      <NotificationIcon
                        type={item.attribute?.icon?.type || 'info'}
                        iconName={item.attribute?.icon?.iconName || 'info-circle'}
                      />
                    }
                    title={item.title}
                    description={
                      <FullWidthSpace direction="vertical">
                        <span>{item.content}</span>
                        <DateTimeTitle>{readStatus(item)}</DateTimeTitle>
                      </FullWidthSpace>
                    }
                  />
                </ListItemStyle>
              )}
            >
              {loading && hasMore && <LoadingIcon />}
            </List>
          </InfiniteScroll>
        </Col>
      </Row>
    </ContentBlock>
  )
}

export default NotificationList

const DateTimeTitle = styled.span`
  color: #2699fb;
`

const ListItemStyle = styled(List.Item)`
  cursor: pointer;
  padding-left: 8px;
  padding-right: 8px;
  &:hover {
    box-shadow: 0 0 4px #c1b7b7;
  }
`
